/**
 * Consent-gated tiered persistence (attr-security ruling, design §2.2).
 *
 * The visitor UUID is ALWAYS generated in-memory; *persisting* it is gated:
 *   - analytics consent gates the visitor/session analytics identifier
 *   - marketing consent gates marketing/cross-domain identifiers
 *   - consentMode (strict|balanced|permissive) sets the default
 *
 * Reading a pre-existing identifier is consent-gated too: otherwise a denied or
 * not-yet-known visitor could be correlated through an identifier written during
 * an earlier consented visit. On an in-session analytics grant we PROMOTE the
 * current in-memory UUID into persistent storage (same UUID → continuity).
 */
import type { ConsentState, ResolvedConfig } from './types';
import { analyticsGranted } from './consent';
import {
  delCookie,
  getCookie,
  id,
  nowIso,
  probeStorage,
  setCookie,
  storageDel,
  storageGet,
  storageSet,
  weakEntropy,
} from './util';

const VID_MAX_AGE = 60 * 60 * 24 * 400; // ~13 months

export interface SessionResolve {
  sessionId: string;
  isNew: boolean;
}

export class PixelStore {
  private cfg: ResolvedConfig;
  private getConsent: () => ConsentState;
  private ls: Storage | null;
  private ss: Storage | null;
  private cookieOk: boolean;

  private visitorId = '';
  private persistedVid = false;
  private memorySessionId = '';
  private memoryLastActivity = 0;
  private memoryFirstTouch: string | null = null;
  private memoryLastTouch: string | null = null;

  constructor(cfg: ResolvedConfig, getConsent: () => ConsentState) {
    this.cfg = cfg;
    this.getConsent = getConsent;
    const noStore = cfg.storageMode === 'none' || cfg.storageMode === 'memory';
    this.ls =
      noStore || cfg.storageMode === 'cookie'
        ? null
        : probeStorage(() => (globalThis as { localStorage?: Storage }).localStorage);
    this.ss = noStore
      ? null
      : probeStorage(() => (globalThis as { sessionStorage?: Storage }).sessionStorage);
    this.cookieOk =
      !noStore && cfg.storageMode !== 'localStorage' && this.probeCookie();
  }

  private storageKey(key: string): string {
    return this.cfg.storageNamespace ? `${key}_${this.cfg.storageNamespace}` : key;
  }

  /** True when persistent (cross-session) storage of the analytics ID is permitted. */
  persistAnalyticsAllowed(): boolean {
    if (this.cfg.storageMode === "none" || this.cfg.storageMode === "memory")
      return false;
    if (this.getConsent().analytics === false) return false;
    if (this.cfg.consentMode === "permissive") return true; // An explicit refusal always overrides the default.
    return analyticsGranted(this.getConsent()); // strict & balanced require explicit grant
  }

  get idConfidence(): 'high' | 'low' {
    return this.persistedVid && !weakEntropy ? 'high' : 'low';
  }

  /* ----------------------------- visitor ----------------------------- */

  resolveVisitorId(): string {
    const persistentAllowed = this.persistAnalyticsAllowed();

    // Only consult cross-session identifiers when the current consent state
    // permits their use. Explicit denial also removes stale analytics state.
    if (persistentAllowed) {
      let existing = this.cookieOk ? getCookie(this.storageKey('tmg_vid')) : null;
      if (!existing) existing = storageGet(this.ls, this.storageKey('tmg_vid'));
      if (existing) {
        this.visitorId = existing;
        this.persistedVid = true;
        this.writeVid(existing); // re-affirm cookie freshness
        return existing;
      }
    } else if (this.getConsent().analytics === false) {
      this.clearAnalyticsState();
    }

    // No usable persisted id — generate in-memory; persist only if permitted.
    this.visitorId = id('vis');
    if (persistentAllowed) this.writeVid(this.visitorId);
    else this.persistedVid = false;
    return this.visitorId;
  }

  /** Adopt a server-authoritative id and persist to whatever tier is permitted. */
  adoptVisitorId(serverId: string): void {
    if (!serverId || serverId === this.visitorId) return;
    this.visitorId = serverId;
    if (this.persistAnalyticsAllowed()) this.writeVid(serverId);
    else this.persistedVid = false;
  }

  /** In-session promotion: persist the existing in-memory UUID once consent allows. */
  promoteIfAllowed(): void {
    if (!this.persistedVid && this.visitorId && this.persistAnalyticsAllowed()) {
      this.writeVid(this.visitorId);
    }
  }

  /**
   * Apply a live consent change. Returns true when an identifier that had been
   * persistent was rotated, so the caller can rotate its session id as well.
   */
  reconcileConsent(): boolean {
    if (this.persistAnalyticsAllowed()) {
      this.promoteIfAllowed();
      return false;
    }

    const rotate = this.persistedVid;
    // An active persistent id must be retired on withdrawal. Explicit denial
    // also clears a dormant id that was ignored during an unknown-consent boot.
    if (rotate || this.getConsent().analytics === false) this.clearAnalyticsState();
    this.persistedVid = false;
    if (rotate) this.visitorId = id('vis');
    return rotate;
  }

  private writeVid(value: string): void {
    let ok = false;
    if (this.cookieOk) ok = setCookie(this.storageKey('tmg_vid'), value, VID_MAX_AGE, this.cfg.cookieDomain) || ok;
    if (this.ls) ok = storageSet(this.ls, this.storageKey('tmg_vid'), value) || ok;
    this.persistedVid = ok;
  }

  private clearAnalyticsState(): void {
    storageDel(this.ls, this.storageKey('tmg_vid'));
    storageDel(this.ls, this.storageKey('tmg_ft'));
    storageDel(this.ls, this.storageKey('tmg_q'));
    storageDel(this.ss, this.storageKey('tmg_sid'));
    storageDel(this.ss, this.storageKey('tmg_act'));
    storageDel(this.ss, this.storageKey('tmg_ft'));
    storageDel(this.ss, this.storageKey('tmg_lt'));
    this.memorySessionId = '';
    this.memoryLastActivity = 0;
    this.memoryFirstTouch = null;
    this.memoryLastTouch = null;

    this.clearCookie(this.storageKey('tmg_vid'));
    this.clearCookie(this.storageKey('tmg_sid'));
    this.clearCookie(this.storageKey('tmg_act'));
  }

  private clearCookie(name: string): void {
    // Clear both host-only and configured-domain variants. A site's cookie
    // configuration can change between visits, leaving either form behind.
    delCookie(name);
    if (this.cfg.cookieDomain) delCookie(name, this.cfg.cookieDomain);
  }

  /* ----------------------------- session ----------------------------- */

  resolveSession(): SessionResolve {
    const now = Date.now();
    const timeout = this.cfg.sessionTimeoutMs;
    const persistentAllowed = this.persistAnalyticsAllowed();
    // Browser storage is consulted only while current consent permits it.
    // Otherwise the session exists only inside this pixel runtime.
    let sid = persistentAllowed
      ? storageGet(this.ss, this.storageKey('tmg_sid')) || (this.cookieOk ? getCookie(this.storageKey('tmg_sid')) : null)
      : this.memorySessionId || null;
    const lastAct = parseInt(
      (persistentAllowed
        ? storageGet(this.ss, this.storageKey('tmg_act')) || (this.cookieOk ? getCookie(this.storageKey('tmg_act')) || '' : '')
        : String(this.memoryLastActivity || '')) ||
        '0',
      10
    );
    const expired = !sid || !lastAct || now - lastAct > timeout || this.midnightRolled(lastAct, now);
    let isNew = false;
    if (expired) {
      sid = id('ses');
      isNew = true;
    }
    this.touchSession(sid as string, now);
    return { sessionId: sid as string, isNew };
  }

  touchSession(sid?: string, now?: number): void {
    const t = now || Date.now();
    const persistentAllowed = this.persistAnalyticsAllowed();
    const sess = sid || this.memorySessionId || (persistentAllowed ? storageGet(this.ss, this.storageKey('tmg_sid')) : null);
    if (!sess) return;
    this.memorySessionId = sess;
    this.memoryLastActivity = t;
    if (persistentAllowed) {
      storageSet(this.ss, this.storageKey('tmg_sid'), sess);
      storageSet(this.ss, this.storageKey('tmg_act'), '' + t);
    }
    if (this.cookieOk && persistentAllowed) {
      const idle = Math.ceil(this.cfg.sessionTimeoutMs / 1000);
      setCookie(this.storageKey('tmg_sid'), sess, idle, this.cfg.cookieDomain);
      setCookie(this.storageKey('tmg_act'), '' + t, idle, this.cfg.cookieDomain);
    }
  }

  private midnightRolled(lastAct: number, now: number): boolean {
    try {
      return new Date(lastAct).getUTCDate() !== new Date(now).getUTCDate();
    } catch {
      return false;
    }
  }

  /* --------------------------- touch records -------------------------- */

  readFirstTouch(): string | null {
    return this.persistAnalyticsAllowed()
      ? storageGet(this.ls, this.storageKey('tmg_ft'))
      : this.memoryFirstTouch;
  }

  writeFirstTouch(json: string): void {
    // First-touch persistence follows the analytics tier; else runtime memory.
    if (this.persistAnalyticsAllowed()) storageSet(this.ls, this.storageKey('tmg_ft'), json);
    else this.memoryFirstTouch = json;
  }

  readLastTouch(): string | null {
    return this.persistAnalyticsAllowed() ? storageGet(this.ss, this.storageKey('tmg_lt')) : this.memoryLastTouch;
  }

  writeLastTouch(json: string): void {
    if (this.persistAnalyticsAllowed()) storageSet(this.ss, this.storageKey('tmg_lt'), json);
    else this.memoryLastTouch = json;
  }

  /* ----------------------------- queue -------------------------------- */

  /** Offline queue is persistent only when the analytics tier permits; else memory. */
  queuePersistAllowed(): boolean {
    return !!this.ls && this.persistAnalyticsAllowed();
  }

  readQueue(): string | null {
    return this.queuePersistAllowed() ? storageGet(this.ls, this.storageKey('tmg_q')) : null;
  }

  writeQueue(json: string): void {
    if (this.queuePersistAllowed()) storageSet(this.ls, this.storageKey('tmg_q'), json);
  }

  private probeCookie(): boolean {
    try {
      document.cookie = '__tmg_c__=1; Path=/; SameSite=Lax';
      const ok = /(?:^|; )__tmg_c__=1/.test(document.cookie);
      document.cookie = '__tmg_c__=; Path=/; Max-Age=0; SameSite=Lax';
      return ok;
    } catch {
      return false;
    }
  }

  get vid(): string {
    return this.visitorId;
  }

  nowIso(): string {
    return nowIso();
  }
}
